import React, { useState, useEffect, useCallback } from 'react';
import { Download, Filter, FileText, Tag, TrendingDown, Shield, Wrench, BarChart2, RefreshCw } from 'lucide-react';
import { mastersApi, reportsApi, txApi } from '../services/api';
import { Table, Pagination, Tabs, Spinner, Field } from '../components/common';
import { fmt, StatusBadge } from '../utils/helpers';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import toast from 'react-hot-toast';

const reportsApi2 = {
  insurance:     p => reportsApi.insurance(p),
  transfers:     p => reportsApi.transfers(p),
  depreciation:  p => reportsApi.depreciation(p),
  tagStatus:     () => reportsApi.tagStatus(),
  ageAnalysis:   () => reportsApi.ageAnalysis(),
  maintenance:   p => reportsApi.maintenance(p),
  assetRegister: p => reportsApi.assetRegister(p),
  exportExcel:   (type, params={}) => reportsApi.exportExcel(type, params),
};

export function AssetRegisterReport() {
  const [data,     setData]    = useState([]);
  const [summary,  setSummary] = useState({});
  const [loading,  setLoading] = useState(false);
  const [page,     setPage]    = useState(1);
  const [total,    setTotal]   = useState(0);
  const [categories,setCats]   = useState([]);
  const [locations, setLocs]   = useState([]);
  const [filters,  setFilters] = useState({ status:'active', major_category_id:'', location_id:'' });

  useEffect(() => {
    mastersApi.majorCategories({limit:200}).then(r=>setCats(r.data||[]));
    mastersApi.locations({limit:500}).then(r=>setLocs(r.data||[]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await reportsApi2.assetRegister({ ...filters, page, limit:50 });
      setData(r.data||[]); setSummary(r.summary||{}); setTotal(parseInt(r.summary?.count||0));
    } catch {} finally { setLoading(false); }
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  const columns = [
    { key:'asset_code',     title:'Code',     render:v=><span style={{fontFamily:'monospace',fontWeight:600,color:'var(--primary)',fontSize:'var(--fs-xs)'}}>{v}</span> },
    { key:'asset_name',     title:'Name',     render:v=>fmt.truncate(v,36) },
    { key:'major_category', title:'Category', render:(v,r)=><><div style={{fontSize:'var(--fs-xs)'}}>{v}</div><div style={{fontSize:10,color:'var(--text-muted)'}}>{r.minor_category}</div></> },
    { key:'location',       title:'Location', render:(v,r)=><><span style={{fontSize:'var(--fs-xs)'}}>{v||'—'}</span>{r.sub_location && <><br/><span style={{fontSize:10,color:'var(--text-muted)'}}>↳ {r.sub_location}</span></>}</> },
    { key:'purchase_date',  title:'Cap. Date',render:v=>fmt.date(v) },
    { key:'purchase_cost',  title:'Gross',    align:'right', render:v=><span style={{fontFamily:'monospace',fontSize:'var(--fs-xs)'}}>{fmt.currency(v)}</span> },
    { key:'current_acc_dep',title:'Acc.Dep',  align:'right', render:v=><span style={{fontFamily:'monospace',fontSize:'var(--fs-xs)',color:'var(--text-muted)'}}>{fmt.currency(v)}</span> },
    { key:'current_nbv',    title:'NBV',      align:'right', render:v=><span style={{fontFamily:'monospace',fontSize:'var(--fs-xs)',fontWeight:600,color:'var(--accent)'}}>{fmt.currency(v)}</span> },
    { key:'asset_status',   title:'Status',   render:v=><StatusBadge status={v}/> },
    { key:'is_tagged',      title:'Tagged',   align:'center', render:(v,r)=> r.is_taggable===false && !v
        ? <span className="badge badge-gray" style={{fontSize:10}} title="Marked non-taggable">N/A</span>
        : <span className={`badge ${v?'badge-green':'badge-red'}`} style={{fontSize:10}}>{v?'✓':'✗'}</span> },
  ];

  return (
    <div>
      <div className="page-header">
        <div><h1 className="page-title">Asset Register</h1></div>
        <div className="page-actions"><button className="btn btn-secondary" onClick={()=>reportsApi2.exportExcel('asset_register',{status:filters.status})}><Download size={14}/> Export Excel</button></div>
      </div>
      {summary.count && (
        <div className="kpi-grid" style={{gridTemplateColumns:'repeat(4,1fr)',marginBottom:16}}>
          {[{ l:'Assets',v:fmt.number(summary.count)},{ l:'Gross Block',v:fmt.currency(summary.total_cost)},{ l:'Acc. Dep.',v:fmt.currency(summary.total_dep)},{ l:'Net Block',v:fmt.currency(summary.total_nbv)}].map(k=>(
            <div key={k.l} className="kpi-card"><div className="kpi-label">{k.l}</div><div className="kpi-value" style={{fontSize:'var(--fs-xl)'}}>{k.v}</div></div>
          ))}
        </div>
      )}
      <div className="card">
        <div style={{padding:'12px 20px',borderBottom:'1px solid var(--border-light)',display:'flex',gap:10,flexWrap:'wrap'}}>
          <select className="form-control" style={{width:140}} value={filters.status} onChange={e=>setFilters(f=>({...f,status:e.target.value}))}>
            <option value="active">Active</option><option value="">All</option><option value="scrapped">Scrapped</option>
          </select>
          <select className="form-control" style={{width:180}} value={filters.major_category_id} onChange={e=>setFilters(f=>({...f,major_category_id:e.target.value}))}>
            <option value="">All Categories</option>
            {categories.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select className="form-control" style={{width:160}} value={filters.location_id} onChange={e=>setFilters(f=>({...f,location_id:e.target.value}))}>
            <option value="">All Locations</option>
            {locations.map(l=><option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
          <button className="btn btn-primary btn-sm" onClick={()=>setPage(1)}><Filter size={12}/> Apply</button>
        </div>
        <Table columns={columns} data={data} loading={loading}/>
        {total>0&&<Pagination page={page} totalPages={Math.ceil(total/50)} total={total} limit={50} onPage={setPage}/>}
      </div>
    </div>
  );
}

export function ReconciliationReport() {
  return (
    <div>
      <div className="page-header"><div><h1 className="page-title">Book vs Physical Reconciliation</h1></div></div>
      <div className="card"><div className="card-body">
        <p style={{fontSize:'var(--fs-sm)',color:'var(--text-muted)',textAlign:'center',padding:'32px 0'}}>Select a completed audit session from the Audit module to generate the reconciliation report.</p>
      </div></div>
    </div>
  );
}

// ─── All Reports Hub ──────────────────────────────────────
export default function AllReports() {
  const [tab, setTab] = useState('insurance');
  return (
    <div>
      <div className="page-header"><div><h1 className="page-title">Reports</h1><p className="page-sub">Financial, compliance, and operational reports</p></div></div>
      <Tabs tabs={[
        {key:'insurance',  label:'Insurance'},
        {key:'transfers',  label:'Transfers'},
        {key:'depreciation',label:'Depreciation'},
        {key:'tags',       label:'Tag Status'},
        {key:'age',        label:'Age Analysis'},
        {key:'maintenance',label:'Maintenance'},
      ]} active={tab} onChange={setTab}/>
      {tab==='insurance'   && <InsuranceReport/>}
      {tab==='transfers'   && <TransferHistoryReport/>}
      {tab==='depreciation'&& <DepreciationReport/>}
      {tab==='tags'        && <TagStatusReport/>}
      {tab==='age'         && <AgeAnalysisReport/>}
      {tab==='maintenance' && <MaintenanceReport/>}
    </div>
  );
}

// ─── Insurance Report ─────────────────────────────────────
function InsuranceReport() {
  const [data,    setData]    = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [days,    setDays]    = useState(90);
  const [expired, setExpired] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await reportsApi2.insurance({ days, expired }); setData(r.data||[]); setSummary(r.summary||{}); }
    catch {} finally { setLoading(false); }
  }, [days, expired]);

  useEffect(()=>{ load(); },[load]);

  const cols = [
    { key:'asset_code',    title:'Asset',    render:(v,r)=><><span style={{fontFamily:'monospace',fontWeight:600,color:'var(--primary)',fontSize:'var(--fs-xs)'}}>{v}</span><br/><span style={{fontSize:11,color:'var(--text-muted)'}}>{fmt.truncate(r.asset_name,28)}</span></> },
    { key:'policy_no',     title:'Policy No',render:v=><span style={{fontFamily:'monospace',fontSize:'var(--fs-xs)'}}>{v}</span> },
    { key:'insurer_name',  title:'Insurer' },
    { key:'coverage_amount',title:'Coverage',align:'right',render:v=><span style={{fontFamily:'monospace',fontSize:'var(--fs-xs)'}}>{fmt.currency(v)}</span>},
    { key:'premium_amount',title:'Premium',  align:'right',render:v=><span style={{fontFamily:'monospace',fontSize:'var(--fs-xs)'}}>{fmt.currency(v)}</span>},
    { key:'end_date',      title:'Expires',  render:v=>fmt.date(v)},
    { key:'days_remaining',title:'Days Left',align:'right',render:v=><span style={{fontWeight:600,color:v<=30?'var(--danger)':v<=60?'var(--warning)':'var(--accent)'}}>{v}</span>},
  ];

  return (
    <div>
      <div style={{display:'flex',gap:12,marginBottom:16,alignItems:'center',flexWrap:'wrap'}}>
        <select className="form-control" style={{width:160}} value={days} onChange={e=>setDays(e.target.value)}>
          <option value={30}>Due in 30 days</option><option value={60}>Due in 60 days</option><option value={90}>Due in 90 days</option><option value={180}>Due in 180 days</option>
        </select>
        <label style={{display:'flex',alignItems:'center',gap:6,fontSize:'var(--fs-sm)',cursor:'pointer'}}>
          <input type="checkbox" checked={expired} onChange={e=>setExpired(e.target.checked)}/> Show expired only
        </label>
        <button className="btn btn-secondary btn-sm" onClick={()=>reportsApi2.exportExcel('insurance')}><Download size={12}/> Export</button>
      </div>
      {summary.count>0&&(
        <div className="kpi-grid" style={{gridTemplateColumns:'repeat(3,1fr)',marginBottom:16}}>
          <div className="kpi-card"><div className="kpi-label">Policies Due</div><div className="kpi-value" style={{color:'var(--danger)'}}>{summary.count}</div></div>
          <div className="kpi-card"><div className="kpi-label">Total Coverage</div><div className="kpi-value" style={{fontSize:'var(--fs-xl)'}}>{fmt.currency(summary.total_coverage)}</div></div>
          <div className="kpi-card"><div className="kpi-label">Annual Premium</div><div className="kpi-value" style={{fontSize:'var(--fs-xl)'}}>{fmt.currency(summary.total_premium)}</div></div>
        </div>
      )}
      <div className="card"><Table columns={cols} data={data} loading={loading} emptyText="No insurance records due in selected period"/></div>
    </div>
  );
}

// ─── Transfer History ─────────────────────────────────────
export function TransferHistoryReport() {
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage]       = useState(1);
  const [total, setTotal]     = useState(0);
  const [filters, setFilters] = useState({ from_date:'', to_date:'', status:'' });

  const load = useCallback(async()=>{
    setLoading(true);
    try {
      const r = await reportsApi2.transfers({ ...Object.fromEntries(Object.entries(filters).filter(([,v])=>v)), page, limit:50 });
      setData(r.data||[]); setTotal(parseInt(r.pagination?.total||0));
    } catch {} finally { setLoading(false); }
  },[filters,page]);

  useEffect(()=>{load();},[load]);

  const cols = [
    { key:'transfer_no',   title:'Transfer No',render:v=><span style={{fontFamily:'monospace',fontWeight:600,fontSize:'var(--fs-xs)'}}>{v}</span> },
    { key:'asset_code',    title:'Asset',      render:(v,r)=><><span style={{fontWeight:500}}>{v}</span><br/><span style={{fontSize:11,color:'var(--text-muted)'}}>{fmt.truncate(r.asset_name,25)}</span></> },
    { key:'from_location', title:'From',       render:v=><span style={{fontSize:'var(--fs-xs)'}}>{v||'—'}</span> },
    { key:'to_location',   title:'To',         render:v=><span style={{fontSize:'var(--fs-xs)',color:'var(--accent)',fontWeight:500}}>{v||'—'}</span> },
    { key:'transfer_date', title:'Date',       render:v=>fmt.date(v) },
    { key:'requested_by',  title:'Requested By', render:v=><span style={{fontSize:'var(--fs-xs)'}}>{v||'—'}</span> },
    { key:'approved_by',   title:'Approved By',  render:v=><span style={{fontSize:'var(--fs-xs)'}}>{v||'—'}</span> },
    { key:'status',        title:'Status',     render:v=><StatusBadge status={v}/> },
  ];

  return (
    <div>
      <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap',alignItems:'flex-end'}}>
        <Field label="From Date" style={{margin:0,flex:'none'}}><input className="form-control" type="date" value={filters.from_date} onChange={e=>setFilters(f=>({...f,from_date:e.target.value}))} /></Field>
        <Field label="To Date"   style={{margin:0,flex:'none'}}><input className="form-control" type="date" value={filters.to_date}   onChange={e=>setFilters(f=>({...f,to_date:e.target.value}))} /></Field>
        <Field label="Status" style={{margin:0,flex:'none'}}>
          <select className="form-control" style={{width:130}} value={filters.status} onChange={e=>setFilters(f=>({...f,status:e.target.value}))}>
            <option value="">All</option><option value="approved">Approved</option><option value="rejected">Rejected</option><option value="pending">Pending</option>
          </select>
        </Field>
        <button className="btn btn-secondary btn-sm" style={{alignSelf:'flex-end'}} onClick={()=>reportsApi2.exportExcel('transfers')}><Download size={12}/> Export</button>
      </div>
      <div className="card">
        <Table columns={cols} data={data} loading={loading}/>
        {total>0&&<Pagination page={page} totalPages={Math.ceil(total/50)} total={total} limit={50} onPage={setPage}/>}
      </div>
    </div>
  );
}

// ─── Depreciation Report ──────────────────────────────────
export function DepreciationReport() {
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [data,    setData]    = useState([]);
  const [totals,  setTotals]  = useState({});
  const [loading, setLoading] = useState(false);
  const [page,    setPage]    = useState(1);

  useEffect(() => { txApi.depSchedule().then(r=>setPeriods(r.data||[])); }, []);

  const load = useCallback(async()=>{
    if (!selectedPeriod) return;
    setLoading(true);
    try { const r = await reportsApi2.depreciation({ period_id:selectedPeriod, page, limit:50 }); setData(r.data||[]); setTotals(r.summary||{}); }
    catch {} finally { setLoading(false); }
  },[selectedPeriod,page]);

  useEffect(()=>{load();},[load]);

  const cols = [
    { key:'asset_code',      title:'Asset',      render:(v,r)=><><span style={{fontFamily:'monospace',fontWeight:600,color:'var(--primary)',fontSize:'var(--fs-xs)'}}>{v}</span><br/><span style={{fontSize:11,color:'var(--text-muted)'}}>{fmt.truncate(r.asset_name,28)}</span></> },
    { key:'major_category',  title:'Category',   render:v=><span style={{fontSize:'var(--fs-xs)'}}>{v||'—'}</span> },
    { key:'effective_method',title:'Method',     render:v=><span className="badge badge-blue">{v}</span> },
    { key:'opening_nbv_comp',title:'Opening NBV (CA)',align:'right',render:v=><span style={{fontFamily:'monospace',fontSize:'var(--fs-xs)'}}>{fmt.currency(v)}</span>},
    { key:'dep_for_period_comp',title:'Dep (CA)',align:'right',render:v=><span style={{fontFamily:'monospace',fontSize:'var(--fs-xs)',color:'var(--danger)'}}>{fmt.currency(v)}</span>},
    { key:'closing_nbv_comp',title:'Closing NBV (CA)',align:'right',render:v=><span style={{fontFamily:'monospace',fontSize:'var(--fs-xs)',fontWeight:600}}>{fmt.currency(v)}</span>},
    { key:'dep_for_period_it',title:'Dep (IT)',  align:'right',render:v=><span style={{fontFamily:'monospace',fontSize:'var(--fs-xs)',color:'var(--warning)'}}>{fmt.currency(v)}</span>},
    { key:'timing_diff',     title:'Timing Diff',align:'right',render:v=><span style={{fontFamily:'monospace',fontSize:'var(--fs-xs)',color:v>0?'var(--danger)':v<0?'var(--accent)':'var(--text-muted)'}}>{fmt.currency(v)}</span>},
    { key:'is_fully_depreciated',title:'Fully Dep.',align:'center',render:v=>v?<span className="badge badge-amber">Yes</span>:<span style={{color:'var(--text-muted)',fontSize:11}}>—</span>},
  ];

  return (
    <div>
      <div style={{display:'flex',gap:12,marginBottom:16,alignItems:'center',flexWrap:'wrap'}}>
        <select className="form-control" style={{width:220}} value={selectedPeriod} onChange={e=>{setSelectedPeriod(e.target.value);setPage(1);}}>
          <option value="">Select Period…</option>
          {periods.map(p=><option key={p.id} value={p.id}>{p.period_name} ({p.period_type})</option>)}
        </select>
        {selectedPeriod&&<button className="btn btn-secondary btn-sm" onClick={()=>reportsApi2.exportExcel('depreciation',{period_id:selectedPeriod})}><Download size={12}/> Export Excel</button>}
      </div>
      {Object.keys(totals).length>0&&(
        <div className="kpi-grid" style={{gridTemplateColumns:'repeat(4,1fr)',marginBottom:16}}>
          {[
            {l:'Opening NBV (CA)', v:fmt.currency(totals.total_opening_nbv)},
            {l:'Dep for Period (CA)',v:fmt.currency(totals.total_dep_comp)},
            {l:'Closing NBV (CA)', v:fmt.currency(totals.total_closing_nbv)},
            {l:'Dep for Period (IT)',v:fmt.currency(totals.total_dep_it)},
          ].map(k=><div key={k.l} className="kpi-card"><div className="kpi-label">{k.l}</div><div className="kpi-value" style={{fontSize:'var(--fs-xl)'}}>{k.v}</div></div>)}
        </div>
      )}
      <div className="card">
        {!selectedPeriod ? <div className="empty-state" style={{padding:32}}><TrendingDown size={28} style={{opacity:.3,margin:'0 auto 8px'}}/><p>Select a depreciation period to view the schedule</p></div>
          : <Table columns={cols} data={data} loading={loading}/>}
      </div>
    </div>
  );
}

// ─── Tag Status Report ────────────────────────────────────
function TagStatusReport() {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { reportsApi2.tagStatus().then(r=>{setData(r.data);setLoading(false);}); }, []);
  if (loading) return <div className="loading-center"><div className="spinner"/></div>;
  if (!data)   return null;
  const { summary, by_category, untagged_assets } = data;
  const pieData = [
    { name:'RFID',      value:parseInt(summary?.rfid||0),     fill:'#5A3DBB' },
    { name:'QR Code',   value:parseInt(summary?.qr_code||0),  fill:'#1A5FA8' },
    { name:'Barcode',   value:parseInt(summary?.barcode||0),  fill:'#0F8A6E' },
    { name:'Multiple',  value:parseInt(summary?.multiple_tags||0), fill:'#B56A00' },
    { name:'Untagged',  value:parseInt(summary?.untagged||0), fill:'#C93030' },
  ].filter(d=>d.value>0);
  return (
    <div>
      <div className="kpi-grid" style={{gridTemplateColumns:'repeat(6,1fr)',marginBottom:20}}>
        {[{l:'Total Tagged',v:summary?.tagged,c:'var(--accent)'},{l:'Untagged',v:summary?.untagged,c:'var(--danger)'},{l:'RFID',v:summary?.rfid,c:'#5A3DBB'},{l:'QR Code',v:summary?.qr_code,c:'var(--primary)'},{l:'Barcode',v:summary?.barcode,c:'var(--accent)'},{l:'Non-taggable',v:summary?.non_taggable,c:'var(--text-muted)'}].map(k=>(
          <div key={k.l} className="kpi-card"><div className="kpi-label">{k.l}</div><div className="kpi-value" style={{color:k.c}}>{fmt.number(k.v)}</div></div>
        ))}
      </div>
      <div style={{display:'grid',gridTemplateColumns:'280px 1fr',gap:20,marginBottom:20}}>
        <div className="card"><div className="card-header"><span className="card-title">Tag Distribution</span></div><div className="card-body">
          <ResponsiveContainer width="100%" height={200}><PieChart><Pie data={pieData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({name,percent})=>`${name} ${(percent*100).toFixed(0)}%`} labelLine={false}>{pieData.map((d,i)=><Cell key={i} fill={d.fill}/>)}</Pie><Tooltip/></PieChart></ResponsiveContainer>
        </div></div>
        <div className="card"><div className="card-header"><span className="card-title">By Category</span></div><Table columns={[
          {key:'category',title:'Category',render:v=><span style={{fontWeight:500}}>{v}</span>},
          {key:'tagged',title:'Tagged',align:'right',render:v=><span style={{color:'var(--accent)',fontWeight:600}}>{fmt.number(v)}</span>},
          {key:'untagged',title:'Untagged',align:'right',render:v=><span style={{color:v>0?'var(--danger)':'var(--text-muted)',fontWeight:v>0?600:400}}>{fmt.number(v)}</span>},
          {key:'tagged',title:'Progress',render:(v,r)=>{
            const pct=parseInt(v)/(parseInt(v)+parseInt(r.untagged)||1)*100;
            return <div style={{width:80}}><div style={{height:5,background:'var(--bg)',borderRadius:3,overflow:'hidden'}}><div style={{height:'100%',width:`${pct}%`,background:pct>=100?'var(--accent)':'var(--primary)',borderRadius:3}}/></div></div>;
          }},
        ]} data={by_category||[]}/></div>
      </div>
      {untagged_assets?.length>0&&(
        <div className="card"><div className="card-header"><span className="card-title">Untagged Assets</span><span style={{fontSize:'var(--fs-xs)',color:'var(--text-muted)'}}>{untagged_assets.length} assets</span></div>
          <Table columns={[
            {key:'asset_code',title:'Code',render:v=><span style={{fontFamily:'monospace',fontWeight:600,color:'var(--primary)',fontSize:'var(--fs-xs)'}}>{v}</span>},
            {key:'asset_name',title:'Name',render:v=>fmt.truncate(v,40)},
            {key:'major_category',title:'Category',render:v=><span style={{fontSize:'var(--fs-xs)'}}>{v||'—'}</span>},
            {key:'location',title:'Location',render:v=><span style={{fontSize:'var(--fs-xs)'}}>{v||'—'}</span>},
          ]} data={untagged_assets}/>
        </div>
      )}
    </div>
  );
}

// ─── Age Analysis ─────────────────────────────────────────
function AgeAnalysisReport() {
  const [data, setData]     = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { reportsApi2.ageAnalysis().then(r=>{setData(r.data||[]);setLoading(false);}); }, []);
  const COLORS = ['#0F8A6E','#1A5FA8','#B56A00','#C93030','#5A3DBB'];
  return (
    <div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20,marginBottom:20}}>
        <div className="card"><div className="card-header"><span className="card-title">Age-wise Asset Count</span></div><div className="card-body">
          <ResponsiveContainer width="100%" height={220}><BarChart data={data}><XAxis dataKey="age_band" tick={{fontSize:11}}/><YAxis tick={{fontSize:11}}/><Tooltip/><Bar dataKey="asset_count" radius={[4,4,0,0]}>{data.map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]}/>)}</Bar></BarChart></ResponsiveContainer>
        </div></div>
        <div className="card"><div className="card-header"><span className="card-title">Age-wise Gross Block</span></div><div className="card-body">
          <ResponsiveContainer width="100%" height={220}><BarChart data={data}><XAxis dataKey="age_band" tick={{fontSize:11}}/><YAxis tick={{fontSize:11}} tickFormatter={v=>`₹${(v/100000).toFixed(0)}L`}/><Tooltip formatter={v=>fmt.currency(v)}/><Bar dataKey="gross_block" radius={[4,4,0,0]}>{data.map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]}/>)}</Bar></BarChart></ResponsiveContainer>
        </div></div>
      </div>
      <div className="card">
        <Table columns={[
          {key:'age_band',    title:'Age Band'},
          {key:'asset_count', title:'Assets',    align:'right',render:v=>fmt.number(v)},
          {key:'gross_block', title:'Gross Block',align:'right',render:v=>fmt.currency(v)},
          {key:'avg_nbv_pct', title:'Avg NBV %', align:'right',render:v=><span style={{color:v>50?'var(--accent)':v>25?'var(--warning)':'var(--danger)',fontWeight:600}}>{v}%</span>},
        ]} data={data} loading={loading}/>
      </div>
    </div>
  );
}

// ─── Maintenance Report ───────────────────────────────────
function MaintenanceReport() {
  const [data, setData]     = useState([]);
  const [summary, setSummary] = useState({});
  const [byType,  setByType]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ from_date:'', to_date:'', maintenance_type:'' });

  const load = useCallback(async()=>{
    setLoading(true);
    try { const r = await reportsApi2.maintenance(Object.fromEntries(Object.entries(filters).filter(([,v])=>v))); setData(r.data||[]); setSummary(r.summary||{}); setByType(r.by_type||[]); }
    catch {} finally { setLoading(false); }
  },[filters]);

  useEffect(()=>{load();},[load]);

  const cols=[
    {key:'maintenance_no',title:'No.',render:v=><span style={{fontFamily:'monospace',fontSize:'var(--fs-xs)',fontWeight:600}}>{v}</span>},
    {key:'asset_code',    title:'Asset',render:(v,r)=><><span style={{fontWeight:500}}>{v}</span><br/><span style={{fontSize:11,color:'var(--text-muted)'}}>{fmt.truncate(r.asset_name,25)}</span></>},
    {key:'maintenance_type',title:'Type',render:v=><span className={`badge ${v==='amc'?'badge-blue':v==='preventive'?'badge-teal':'badge-gray'}`}>{v}</span>},
    {key:'vendor_name',   title:'Vendor',render:v=><span style={{fontSize:'var(--fs-xs)'}}>{v||'—'}</span>},
    {key:'service_date',  title:'Date',  render:v=>fmt.date(v)},
    {key:'cost',          title:'Cost',  align:'right',render:v=><span style={{fontFamily:'monospace',fontSize:'var(--fs-xs)'}}>{fmt.currency(v)}</span>},
    {key:'next_service_date',title:'Next Due',render:v=><span style={{fontSize:'var(--fs-xs)',color:v&&new Date(v)<new Date()?'var(--danger)':'var(--text-secondary)'}}>{fmt.date(v)}</span>},
  ];
  return (
    <div>
      <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap',alignItems:'flex-end'}}>
        <Field label="From" style={{margin:0}}><input className="form-control" type="date" value={filters.from_date} onChange={e=>setFilters(f=>({...f,from_date:e.target.value}))}/></Field>
        <Field label="To"   style={{margin:0}}><input className="form-control" type="date" value={filters.to_date}   onChange={e=>setFilters(f=>({...f,to_date:e.target.value}))}/></Field>
        <Field label="Type" style={{margin:0}}><select className="form-control" style={{width:140}} value={filters.maintenance_type} onChange={e=>setFilters(f=>({...f,maintenance_type:e.target.value}))}><option value="">All</option><option value="corrective">Corrective</option><option value="preventive">Preventive</option><option value="amc">AMC</option></select></Field>
        <button className="btn btn-secondary btn-sm" style={{alignSelf:'flex-end'}} onClick={()=>reportsApi2.exportExcel('maintenance')}><Download size={12}/> Export</button>
      </div>
      {summary.count>0&&<div className="kpi-grid" style={{gridTemplateColumns:'repeat(2,1fr)',marginBottom:16}}>
        <div className="kpi-card"><div className="kpi-label">Total Services</div><div className="kpi-value">{fmt.number(summary.count)}</div></div>
        <div className="kpi-card"><div className="kpi-label">Total Cost</div><div className="kpi-value" style={{fontSize:'var(--fs-xl)'}}>{fmt.currency(summary.total_cost)}</div></div>
      </div>}
      <div className="card"><Table columns={cols} data={data} loading={loading}/></div>
    </div>
  );
}
